/**
 * @fileoverview Custom Views Manager Component for saving and managing filtered views.
 * Allows users to create, save, share, and manage custom views with specific filters,
 * sorting, and column configurations. Inspired by Frappe CRM's custom views system.
 * @module components/ui/CustomViewsManager
 */

import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    IconButton,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControlLabel,
    Switch,
    Chip,
    Typography,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    ListItemSecondaryAction,
    Tooltip,
    Alert
} from '@mui/material';
import {
    ViewList as ViewIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Star as StarIcon,
    StarBorder as StarBorderIcon,
    Share as ShareIcon,
    FilterList as FilterIcon,
    Sort as SortIcon,
    ViewColumn as ColumnsIcon,
    Save as SaveIcon,
    MoreVert as MoreIcon,
    Check as CheckIcon,
    Lock as LockIcon,
    Public as PublicIcon
} from '@mui/icons-material';

/**
 * Default view configuration used as fallback.
 * @constant {Object}
 */
const DEFAULT_VIEW = {
    id: 'default',
    name: 'Default View',
    isDefault: true,
    isSystem: true,
    filters: {},
    sorting: [],
    columns: [],
    groupBy: null
};

const ViewSelector = ({
    views = [],
    currentView,
    onSelectView,
    onCreateView,
    onEditView,
    onDeleteView,
    onToggleFavorite,
    onShareView,
    disabled = false
}) => {
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [selectedViewForMenu, setSelectedViewForMenu] = useState(null);

    const handleOpenMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const handleSelectView = (view) => {
        onSelectView(view);
        handleCloseMenu();
    };

    const handleOpenViewMenu = (event, view) => {
        event.stopPropagation();
        setSelectedViewForMenu(view);
        setMenuAnchor(event.currentTarget);
    };

    const handleCloseViewMenu = () => {
        setMenuAnchor(null);
        setSelectedViewForMenu(null);
    };

    const favoriteViews = views.filter(v => v.isFavorite);
    const otherViews = views.filter(v => !v.isFavorite);

    return (
        <>
            <Button
                variant="outlined"
                startIcon={<ViewIcon />}
                onClick={handleOpenMenu}
                disabled={disabled}
                sx={{ minWidth: 150 }}
            >
                {currentView?.name || t('views.selectView', 'Select View')}
            </Button>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
                PaperProps={{ sx: { minWidth: 280, maxHeight: 400 } }}
            >
                {favoriteViews.length > 0 && (
                    <>
                        <Typography variant="caption" sx={{ px: 2, py: 1, color: 'text.secondary', display: 'block' }}>
                            {t('views.favorites', 'Favorites')}
                        </Typography>
                        {favoriteViews.map(view => (
                            <MenuItem
                                key={view.id}
                                onClick={() => handleSelectView(view)}
                                selected={currentView?.id === view.id}
                            >
                                <ListItemIcon>
                                    <StarIcon fontSize="small" sx={{ color: 'warning.main' }} />
                                </ListItemIcon>
                                <ListItemText primary={view.name} />
                                {currentView?.id === view.id && <CheckIcon fontSize="small" color="primary" />}
                                <IconButton size="small" onClick={(e) => handleOpenViewMenu(e, view)}>
                                    <MoreIcon fontSize="small" />
                                </IconButton>
                            </MenuItem>
                        ))}
                        <Divider />
                    </>
                )}

                <Typography variant="caption" sx={{ px: 2, py: 1, color: 'text.secondary', display: 'block' }}>
                    {t('views.allViews', 'All Views')}
                </Typography>
                {otherViews.map(view => (
                    <MenuItem
                        key={view.id}
                        onClick={() => handleSelectView(view)}
                        selected={currentView?.id === view.id}
                    >
                        <ListItemIcon>
                            {view.isShared ? <PublicIcon fontSize="small" /> : <ViewIcon fontSize="small" />}
                        </ListItemIcon>
                        <ListItemText 
                            primary={view.name}
                            secondary={view.isSystem ? t('views.system', 'System') : null}
                        />
                        {currentView?.id === view.id && <CheckIcon fontSize="small" color="primary" />}
                        {!view.isSystem && (
                            <IconButton size="small" onClick={(e) => handleOpenViewMenu(e, view)}>
                                <MoreIcon fontSize="small" />
                            </IconButton>
                        )}
                    </MenuItem>
                ))}

                <Divider />
                <MenuItem onClick={() => { handleCloseMenu(); onCreateView(); }}>
                    <ListItemIcon>
                        <AddIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={t('views.createNew', 'Create New View')} />
                </MenuItem>
            </Menu>

            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleCloseViewMenu}
            >
                <MenuItem onClick={() => { onToggleFavorite(selectedViewForMenu); handleCloseViewMenu(); }}>
                    <ListItemIcon>
                        {selectedViewForMenu?.isFavorite ? <StarBorderIcon fontSize="small" /> : <StarIcon fontSize="small" />}
                    </ListItemIcon>
                    <ListItemText primary={selectedViewForMenu?.isFavorite ? t('views.unfavorite', 'Remove from Favorites') : t('views.favorite', 'Add to Favorites')} />
                </MenuItem>
                {!selectedViewForMenu?.isSystem && (
                    <>
                        <MenuItem onClick={() => { onEditView(selectedViewForMenu); handleCloseViewMenu(); }}>
                            <ListItemIcon>
                                <EditIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary={t('common.edit', 'Edit')} />
                        </MenuItem>
                        <MenuItem onClick={() => { onShareView(selectedViewForMenu); handleCloseViewMenu(); }}>
                            <ListItemIcon>
                                <ShareIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary={t('views.share', 'Share')} />
                        </MenuItem>
                        <Divider />
                        <MenuItem onClick={() => { onDeleteView(selectedViewForMenu); handleCloseViewMenu(); }} sx={{ color: 'error.main' }}>
                            <ListItemIcon>
                                <DeleteIcon fontSize="small" color="error" />
                            </ListItemIcon>
                            <ListItemText primary={t('common.delete', 'Delete')} />
                        </MenuItem>
                    </>
                )}
            </Menu>
        </>
    );
};

const ViewEditorDialog = ({
    open,
    onClose,
    onSave,
    view,
    availableColumns = [],
    availableFilters: _availableFilters = []
}) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        name: view?.name || '',
        isShared: view?.isShared || false,
        filters: view?.filters || {},
        columns: view?.columns || [],
        sorting: view?.sorting || [],
        groupBy: view?.groupBy || null
    });
    const [errors, setErrors] = useState({});

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    const handleSave = () => {
        const newErrors = {};
        if (!formData.name.trim()) {
            newErrors.name = t('validation.required', 'This field is required');
        }
        
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        onSave({
            ...view,
            ...formData,
            id: view?.id || `view_${Date.now()}`
        });
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {view?.id ? t('views.editView', 'Edit View') : t('views.createView', 'Create View')}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                        label={t('views.viewName', 'View Name')}
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        error={Boolean(errors.name)}
                        helperText={errors.name}
                        fullWidth
                        autoFocus
                    />

                    <FormControlLabel
                        control={
                            <Switch
                                checked={formData.isShared}
                                onChange={(e) => handleChange('isShared', e.target.checked)}
                            />
                        }
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {formData.isShared ? <PublicIcon fontSize="small" /> : <LockIcon fontSize="small" />}
                                {formData.isShared ? t('views.sharedWithTeam', 'Shared with team') : t('views.private', 'Private')}
                            </Box>
                        }
                    />

                    {availableColumns.length > 0 && (
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                {t('views.visibleColumns', 'Visible Columns')}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {availableColumns.map(col => (
                                    <Chip
                                        key={col.id}
                                        label={col.label}
                                        size="small"
                                        variant={formData.columns.includes(col.id) ? 'filled' : 'outlined'}
                                        onClick={() => {
                                            const newColumns = formData.columns.includes(col.id)
                                                ? formData.columns.filter(c => c !== col.id)
                                                : [...formData.columns, col.id];
                                            handleChange('columns', newColumns);
                                        }}
                                        icon={formData.columns.includes(col.id) ? <CheckIcon /> : null}
                                    />
                                ))}
                            </Box>
                        </Box>
                    )}

                    <Alert severity="info" sx={{ mt: 1 }}>
                        {t('views.filtersTip', 'Filters and sorting from your current view will be saved automatically.')}
                    </Alert>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common.cancel', 'Cancel')}</Button>
                <Button onClick={handleSave} variant="contained" startIcon={<SaveIcon />}>
                    {t('common.save', 'Save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const CustomViewsManager = ({
    views = [DEFAULT_VIEW],
    currentView = DEFAULT_VIEW,
    onViewChange,
    onViewCreate,
    onViewUpdate,
    onViewDelete,
    currentFilters = {},
    currentSorting = [],
    currentColumns = [],
    availableColumns = [],
    availableFilters = [],
    disabled = false
}) => {
    const { t } = useTranslation();
    const [editorOpen, setEditorOpen] = useState(false);
    const [editingView, setEditingView] = useState(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    const handleSelectView = useCallback((view) => {
        onViewChange(view);
        setHasUnsavedChanges(false);
    }, [onViewChange]);

    const handleCreateView = useCallback(() => {
        setEditingView({
            filters: currentFilters,
            sorting: currentSorting,
            columns: currentColumns
        });
        setEditorOpen(true);
    }, [currentFilters, currentSorting, currentColumns]);

    const handleEditView = useCallback((view) => {
        setEditingView(view);
        setEditorOpen(true);
    }, []);

    const handleSaveView = useCallback((viewData) => {
        if (viewData.id && views.find(v => v.id === viewData.id)) {
            onViewUpdate(viewData);
        } else {
            onViewCreate(viewData);
        }
        setEditorOpen(false);
        setEditingView(null);
    }, [views, onViewCreate, onViewUpdate]);

    const handleDeleteView = useCallback((view) => {
        if (window.confirm(t('views.confirmDelete', 'Are you sure you want to delete this view?'))) {
            onViewDelete(view.id);
            if (currentView?.id === view.id) {
                onViewChange(views.find(v => v.isDefault) || views[0]);
            }
        }
    }, [t, onViewDelete, currentView, views, onViewChange]);

    const handleToggleFavorite = useCallback((view) => {
        onViewUpdate({ ...view, isFavorite: !view.isFavorite });
    }, [onViewUpdate]);

    const handleShareView = useCallback((view) => {
        onViewUpdate({ ...view, isShared: !view.isShared });
    }, [onViewUpdate]);

    const handleSaveCurrentAsNew = useCallback(() => {
        setEditingView({
            name: `${currentView?.name || 'View'} (Copy)`,
            filters: currentFilters,
            sorting: currentSorting,
            columns: currentColumns
        });
        setEditorOpen(true);
    }, [currentView, currentFilters, currentSorting, currentColumns]);

    const handleUpdateCurrentView = useCallback(() => {
        if (currentView && !currentView.isSystem) {
            onViewUpdate({
                ...currentView,
                filters: currentFilters,
                sorting: currentSorting,
                columns: currentColumns
            });
            setHasUnsavedChanges(false);
        }
    }, [currentView, currentFilters, currentSorting, currentColumns, onViewUpdate]);

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ViewSelector
                views={views}
                currentView={currentView}
                onSelectView={handleSelectView}
                onCreateView={handleCreateView}
                onEditView={handleEditView}
                onDeleteView={handleDeleteView}
                onToggleFavorite={handleToggleFavorite}
                onShareView={handleShareView}
                disabled={disabled}
            />

            {hasUnsavedChanges && currentView && !currentView.isSystem && (
                <Tooltip title={t('views.saveChanges', 'Save changes to current view')}>
                    <Button
                        size="small"
                        variant="contained"
                        color="warning"
                        startIcon={<SaveIcon />}
                        onClick={handleUpdateCurrentView}
                    >
                        {t('common.save', 'Save')}
                    </Button>
                </Tooltip>
            )}

            <Tooltip title={t('views.saveAsNew', 'Save as new view')}>
                <IconButton size="small" onClick={handleSaveCurrentAsNew}>
                    <AddIcon />
                </IconButton>
            </Tooltip>

            <ViewEditorDialog
                open={editorOpen}
                onClose={() => { setEditorOpen(false); setEditingView(null); }}
                onSave={handleSaveView}
                view={editingView}
                availableColumns={availableColumns}
                availableFilters={availableFilters}
            />
        </Box>
    );
};

export { CustomViewsManager, ViewSelector, ViewEditorDialog, DEFAULT_VIEW };
export default React.memo(CustomViewsManager);
